<template>
  <DashboardLayout :breadcrumbs="breadcrumbs" :hierarchy-params="{document: $route.params.documentId}" ref="dashboard">
    <div class="container is-fluid">
      <div class="level mo mb-7">
        <div class="level-left">
          <div class="control mr-10">
            <h1 class="title is-5">
              <b-tooltip :label="$t('docflow.signed')"
                         class="sign-ico"
                         type="is-light"
                         v-if="detail.pkcs7_info">
                <b-icon icon="check-decagram" size="is-small"></b-icon>
              </b-tooltip>
              {{ $t('docflow.fullDocTypes.incoming') }}
              {{detail.registration_number}}
            </h1>
          </div>
          <div class="control mr-10">
            <b-dropdown aria-role="list">
              <a slot="trigger" role="button" class="button is-small">
                <span>{{ $t('tasks.actions') }}</span>
                <b-icon icon="chevron-down" size="is-small"></b-icon>
              </a>
              <b-dropdown-item @click.native="link(detail.file)" v-if="!detail.is_classified">
                <b-icon icon="download" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.downloadDocument') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="openCard">
                <b-icon icon="card-bulleted" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.card') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="getRegisterCard">
                <b-icon icon="file-document-outline" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.registerCard') }}</span>
              </b-dropdown-item>
              <b-dropdown-item @click.native="remove" v-if="detail.state === 'new' && (isDocflowModerator || isAuthor)">
                <b-icon icon="trash-can" size="is-small" class="mr-5"></b-icon>
                <span>{{ $t('interface.remove') }}</span>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="control">
            <a class="edit mr-10" @click="edit"
               v-if="(['new', 'rejected'].includes(detail.state) && isAuthor)
                      || (detail.change_state === 'approved' && isSupervisor)">
              <b-icon icon="pencil" size="is-small" class="icon"></b-icon>
              {{ $t('interface.edit') }}
            </a>
            <a class="button is-success is-small mr-10"
               v-if="detail.state === 'pending_closing' && isDocflowSupervisor"
               @click="nextState">
              {{ $t('docflow.close') }}
            </a>
            <a @click="nextState" class="button is-small is-success mr-10"
               v-if="isAuthor && ['new', 'rejected'].includes(detail.state)">
              {{ $t('docflow.sendToCreateResolution') }}
            </a>
            <a @click="createResolution" class="button is-small is-success mr-10"
               v-if="isResolutionAuthor && detail.state === 'pending_resolution' && !isResolutionFilled">
              {{ $t('docflow.createResolution') }}
            </a>
            <a @click="editResolution" class="button is-small mr-10"
               v-if="(isResolutionAuthor && ['pending_resolution', 'resolution_rejected'].includes(detail.state))
                      || (detail.change_state === 'approved' && isSupervisor)">
              {{ $t('docflow.editResolution') }}
            </a>
            <a @click="nextState" class="button is-small is-success mr-10"
               v-if="isResolutionAuthor && isResolutionFilled
                      && ['pending_resolution', 'resolution_rejected'].includes(detail.state)">
              {{ $t('docflow.sendForApprove') }}
            </a>
            <a @click="nextState" class="button is-small is-success mr-10"
               v-if="isResolutionApprover && detail.state === 'resolution_filled'">
              {{ $t('docflow.approveResolution') }}
            </a>
            <a class="button is-warning is-small mr-10"
               v-if="(detail.state === 'pending_resolution' && (isResolutionAuthor || isAuthor))
                     || (detail.state === 'resolution_filled' && (isResolutionApprover || isResolutionAuthor || isDocflowAssistant))
                     || (detail.state === 'pending_closing' && isSupervisor)"
               @click="reject">
              {{ $t('interface.return') }}
            </a>
            <a @click="changeRequest = true" class="button is-small is-warning mr-10"
               v-if="canSendChangeRequest && (!detail.change_state || ['done', 'rejected'].includes(detail.change_state))">
              {{ $t('docflow.sendChangeRequest') }}
            </a>
            <a class="button is-danger is-small"
               v-if="!['cancelled', 'closed', 'new'].includes(detail.state) && isAuthor"
               @click="cancel">
              {{ $t('interface.cancel') }}
            </a>
          </div>
        </div>
      </div>
      <div class="flags mb-10">
        <b-tag v-if="detail.is_classified">
          {{ $t(`docflow.is_classified`) }}
        </b-tag>
        <b-tag v-if="detail.is_pending_cancellation">
          {{ $t(`docflow.is_pending_cancellation`) }}
        </b-tag>
        <b-tag v-if="detail.is_informational">
          {{ $t(`docflow.is_informational`) }}
        </b-tag>
      </div>
      <b-tabs @change="tabChange" v-model="tabIndex">
        <b-tab-item :label="$t('interface.information')">
          <div class="columns">
            <div class="column is-7">
              <div class="document description">
                <table class="information">
                  <tr>
                    <td><strong>{{ $t('docflow.author') }}</strong></td>
                    <td>
                      <a @click="userDetail(detail.author.id)" v-if="detail.author">{{ detail.author.initials }}</a>
                    </td>
                    <td><strong>{{ $t('docflow.fields.type') }}</strong></td>
                    <td>{{ detail.type__name }}</td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('tasks.currentState') }}</strong></td>
                    <td>
                      <b-tag :type="`is-${stateColors[detail.state]}`">
                        {{ $t(`workflow.states.${detail.state}`) }}
                      </b-tag>
                    </td>
                    <td><strong>{{ $t('tasks.termDate') }}</strong></td>
                    <td>
                      <template v-if="!detail.is_informational">
                        {{ detail.term_date | localedDate }}
                      </template>
                      <template v-else>&ndash;</template>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.registered_date') }}</strong></td>
                    <td>
                      {{ detail.registered_date | localedDate }}
                    </td>
                    <td><strong>{{ $t('docflow.fields.delivery_type') }}</strong></td>
                    <td>{{ detail.delivery_type__name ? detail.delivery_type__name : '&ndash;' }}</td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.original_document_date') }}</strong></td>
                    <td>
                      {{ detail.original_document_date | localedDate }}
                    </td>
                    <td><strong>{{ $t('docflow.fields.original_document_number') }}</strong></td>
                    <td>{{ detail.original_document_number }}</td>
                  </tr>
                  <tr>
                    <td colspan="4">
                      <strong>{{ $t('docflow.fields.sender') }}</strong>
                      <p>{{ detail.sender ? detail.sender.name : '&ndash;' }}</p>
                    </td>
                  </tr>
                </table>
                <div class="mb-30">
                  <strong>{{ $t('interface.description') }}</strong>
                  <p>{{ detail.description }}</p>
                </div>
                <Correspondents :items="detail.correspondents"
                                :editable="editable"
                                :can-add-external="true"
                                :title="$t('docflow.correspondents')"
                                @add="addCorrespondent"
                                @remove="removeCorrespondent">
                </Correspondents>
                <div class="workflow mb-20 mt-20">
                  <h3 class="title is-6 mb-0">{{ $t('docflow.workflow.title') }}</h3>
                  <b-table :data="detail.assignments" class="maintainers-table">
                    <template slot-scope="props">
                      <b-table-column :label="$t('docflow.workflow.name')" field="name" sortable>
                        <a @click.prevent="assignmentDetail(props.row.id)">{{ props.row.name }}</a>
                      </b-table-column>
                      <b-table-column :label="$t('docflow.workflow.term_date')" width="120">
                        {{ props.row.term_date | localedDate }}
                      </b-table-column>
                    </template>
                    <template slot="empty">
                      <section class="section">
                        <div class="content is-fluid has-text-grey has-text-centered">
                          <p>
                            <span class="has-text-grey-light">{{ $t('docflow.assignmentsTip') }}</span>
                          </p>
                        </div>
                      </section>
                    </template>
                  </b-table>
                </div>
                <!--<Attachments class="mt-15"
                             size="large"
                             @file-upload="uploadFile"
                             @remove-file="removeFile"
                             :editable="isAuthor || isDocflowController"
                             :upload="upload"
                             :title="$t('docflow.attachments')"
                             :data="detail.attachments">
                </Attachments>-->
                <!--<Comments :comments="detail.comments"
                          @create="createComment"
                          @edit="editComment"
                          @remove="removeComment">
                </Comments>-->
              </div>
            </div>
            <div class="column is-5">
              <h3 class="title is-6 mb-15">{{ $t('docflow.progress') }}</h3>
              <Timeline :data="timeline" class="mb-5"/>
              <ChangeRequestControls :data="detail.change_request_states"
                                     @change="loadDetail"
                                     v-if="isSupervisor"/>
            </div>
          </div>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.resolution')">
          <div class="columns">
            <div class="column is-7">
              <div class="resolution description">
                <div v-if="detail.resolution">
                  <div class="mb-10">{{ detail.resolution }}</div>
                </div>
                <table class="information">
                  <tr>
                    <td><strong>{{ $t('docflow.fields.resolution_maintainer') }}</strong></td>
                    <td>
                      {{ detail.resolution_maintainer ? detail.resolution_maintainer.initials : '&ndash;' }}
                    </td>
                    <td><strong>{{ $t('docflow.fields.resolution_author') }}</strong></td>
                    <td>{{ detail.resolution_author ? detail.resolution_author.initials : '&ndash;' }}</td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.supervisor') }}</strong></td>
                    <td>{{ detail.supervisor ? detail.supervisor.initials : '&ndash;' }}</td>
                    <td><strong>{{ $t('docflow.fields.resolution_term_date') }}</strong></td>
                    <td>
                      <template v-if="detail.resolution_term_date">{{ detail.resolution_term_date | localedDate }}
                      </template>
                      <template v-else>&ndash;</template>
                    </td>
                  </tr>
                  <tr>
                    <td><strong>{{ $t('docflow.fields.resolution_approver') }}</strong></td>
                    <td>{{ detail.resolution_approver ? detail.resolution_approver.initials : '&ndash;' }}</td>
                    <td><strong>{{ $t('docflow.fields.register_book') }}</strong></td>
                    <td>
                      {{ detail.register_book__name }}
                    </td>
                  </tr>
                  <tr>
                    <template v-if="detail.parent">
                      <td><strong>{{ $t('docflow.parentDocument') }}</strong></td>
                      <td>
                        <a @click="documentDetail(detail.parent.id, detail.parent.category)">{{
                          detail.parent.registration_number }}</a>
                      </td>
                    </template>
                  </tr>
                </table>
                <h3 class="title is-6 mb-10 mt-15">{{ $t('documents.fields.tasks') }}</h3>
                <b-table :data="detail.resolution_tasks" class="maintainers-table">
                  <template slot-scope="props">
                    <b-table-column :label="$t('issueTracker.executor')" field="name" sortable>
                      {{ props.row.employee.initials }}
                    </b-table-column>
                    <b-table-column :label="$t('dashboard.itemTypes.task')" field="is_read">
                      {{ props.row.description }}
                    </b-table-column>
                    <b-table-column :label="$t('issueTracker.term')" width="120">
                      {{ props.row.term_date | localedDate }}
                    </b-table-column>
                  </template>
                  <template slot="empty">
                    <section class="section">
                      <div class="content is-fluid has-text-grey has-text-centered">
                        <p>
                          <span class="has-text-grey-light">{{ $t('docflow.noResolution') }}</span>
                        </p>
                      </div>
                    </section>
                  </template>
                </b-table>
              </div>
            </div>
          </div>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.documentPlan')">
          <DocflowPlan :edit="isDocflowPlanCreator
                              && ['new', 'pending_resolution', 'resolution_filled'].includes(detail.state)"
                       type="incoming"
                       @refresh="loadDetail"/>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.assignmentsControl')" v-if="isReportsAvailable">
          <AssignmentsControl></AssignmentsControl>
        </b-tab-item>
        <b-tab-item :label="$t('docflow.relatedDocumentsControl')" v-if="isReportsAvailable">
          <RelatedDocumentsControl></RelatedDocumentsControl>
        </b-tab-item>
        <b-tab-item :label="$t('interface.preview')">
          <DocumentPreview></DocumentPreview>
        </b-tab-item>
      </b-tabs>
    </div>
    <b-modal class="dialog"
             :class="{big: noteModalMode === 'closing'}"
             :active.sync="isNoteModalActive"
             has-modal-card>
      <div class="modal-card" style="width: auto">
        <section class="modal-card-body is-titleless">
          <form-select :label="$t('docflow.decisionType')" v-model="decisionType" v-if="noteModalMode === 'closing'">
            <option v-for="item in decisionTypes" :value="item.id">{{item.name}}</option>
          </form-select>
          <textarea class="input mt-10"
                    ref="noteInput"
                    v-model="note"
                    :placeholder="$t('interface.inputNote')">
          </textarea>
        </section>
        <footer class="modal-card-foot">
          <button class="button is-primary" @click="noteSubmit">OK</button>
          <button class="button is-danger" type="button" @click="isNoteModalActive = false">
            {{ $t('interface.cancel') }}
          </button>
        </footer>
      </div>
    </b-modal>
    <DocumentChangeRequest :active.sync="changeRequest" @change="loadDetail"></DocumentChangeRequest>
    <Sign :data="detail.sign_data" ref="eimzo" @output="onSign"></Sign>
  </DashboardLayout>
</template>

<script>
  import {mapState, mapGetters} from 'vuex'
  import {stateColors} from '@/colorTypes'
  import DashboardLayout from '@/components/DashboardLayout'
  import DocumentPreview from '@/components/DocumentPreview'
  import Attachments from '@/components/Attachments'
  import Comments from '@/components/Comments'
  import Sign from '@/components/Sign'
  import DocflowPlan from '@/components/DocflowPlan'
  import Correspondents from '@/components/Correspondents'
  import Timeline from '@/components/Timeline'
  import AssignmentsControl from '@/components/AssignmentsControl'
  import RelatedDocumentsControl from '@/components/RelatedDocumentsControl'
  import DocumentChangeRequest from '@/components/DocumentChangeRequest'
  import ChangeRequestControls from '@/components/ChangeRequestControls'
  import {timelineItem} from '@/constants'
  import {userDetail, documentDetail, assignmentDetail, outputFile, s2ab} from '@/utils'

  const sections = [
    'detail',
    'resolution',
    'plan',
    'assignmentsControl',
    'relatedDocumentsControl',
    'documentPreview'
  ]

  export default {
    components: {
      DashboardLayout,
      Attachments,
      Comments,
      DocflowPlan,
      Correspondents,
      Timeline,
      AssignmentsControl,
      RelatedDocumentsControl,
      DocumentPreview,
      Sign,
      DocumentChangeRequest,
      ChangeRequestControls
    },
    data() {
      return {
        stateColors,
        isNoteModalActive: false,
        noteModalMode: '',
        note: '',
        decisionTypes: [],
        decisionType: null,
        changeRequest: false,
        tabIndex: 0
      }
    },
    methods: {
      documentDetail,
      userDetail,
      assignmentDetail,
      loadDetail() {
        this.$store.dispatch('Docflow/getDetail', {
          documentId: this.$route.params.documentId,
          category: 'incoming'
        })
        this.$refs.dashboard.loadHierarchy()
      },
      edit() {
        this.$router.push({
          name: 'editIncomingDocflow',
          params: {
            documentId: this.$route.params.documentId
          }
        })
      },
      nextState() {
        if (this.detail.state === 'pending_closing') {
          this.$store.dispatch('Docflow/getCloseReport').then(data => {
            this.note = data.text
            this.decisionTypes = data.decision_types
            this.decisionType = data.decision_types[0].id
            this.noteModalMode = 'closing'
            this.isNoteModalActive = true
          })
        } else if (this.detail.is_sign_required) {
          this.$refs.eimzo.sign()
        } else {
          this.$store.dispatch('Docflow/nextState').then(this.loadDetail)
        }
      },
      onSign(pkcs7) {
        this.$store.dispatch('Docflow/nextState', {pkcs7}).then(this.loadDetail)
      },
      createResolution() {
        this.$router.push({
          name: 'createResolution',
          params: {
            documentId: this.$route.params.documentId,
            docType: 'incoming'
          }
        })
      },
      editResolution() {
        this.$router.push({
          name: 'editResolution',
          params: {
            documentId: this.$route.params.documentId,
            docType: 'incoming'
          }
        })
      },
      remove() {
        this.$dialog.confirm({
          title: this.$t('interface.confirm'),
          message: this.$t('interface.areYouSureRemove'),
          confirmText: this.$t('interface.remove'),
          cancelText: this.$t('interface.cancel'),
          type: 'is-danger',
          hasIcon: true,
          onConfirm: () => this.$store.dispatch('Docflow/remove').then(() => {
            this.$router.push({name: 'docflowIncoming'})
          })
        })
      },
      cancel() {
        this.isNoteModalActive = true
        this.noteModalMode = 'cancel'
      },
      reject() {
        this.isNoteModalActive = true
        this.noteModalMode = 'reject'
      },
      noteSubmit() {
        if (this.note.trim().length === 0 && this.noteModalMode !== 'closing') {
          this.$toast.open(this.$t('interface.allRequired'))
          return false
        }
        let note = this.note !== '' ? this.note : null
        if (this.noteModalMode === 'closing') {
          this.$store.dispatch('Docflow/nextState', {
            note,
            decision_type: this.decisionType
          }).then(this.loadDetail)
        } else if (this.noteModalMode === 'cancel') {
          this.$store.dispatch('Docflow/cancel', {note}).then(this.loadDetail)
        } else if (this.noteModalMode === 'reject') {
          this.$store.dispatch('Docflow/reject', note).then(this.loadDetail)
        }
        this.isNoteModalActive = false
        this.note = ''
      },
      addCorrespondent(correspondent) {
        if (correspondent.type !== 'new') {
          let data = {type: correspondent.type}
          data[correspondent.type] = correspondent.id
          this.$store.dispatch("Docflow/addCorrespondent", data)
        } else {
          this.$store.dispatch("Docflow/addCorrespondent", correspondent)
        }
      },
      removeCorrespondent(correspondentId) {
        this.$store.dispatch("Docflow/removeCorrespondent", correspondentId)
      },
      uploadFile({file, type}) {
        this.$store.dispatch('Docflow/uploadFile', {file, type})
      },
      createComment(text) {
        this.$store.dispatch('Docflow/createComment', text)
      },
      editComment(comment) {
        this.$store.dispatch('Docflow/updateComment', comment)
      },
      removeComment(id) {
        this.$store.dispatch('Docflow/removeComment', id)
      },
      tabChange(index) {
        this.$router.push({
          name: 'docflowIncomingDetail',
          params: {
            documentId: this.detail.id,
            section: sections[index]
          }
        })
      },
      link(link) {
        let win = window.open(link, '_blank')
        win.focus()
      },
      getRegisterCard() {
        this.$axios.get(`docflow/direct/${this.detail.hash}/?export=true`).then(response => {
          let data = new Blob([s2ab(atob(response.data))], {type: ''})
          outputFile('register_card.doc', data)
        })
      },
      openCard() {
        let routeData = this.$router.resolve({
          name: 'directDocument', params: {
            hash: this.detail.hash
          }
        })
        window.open(routeData.href, '_blank')
      }
    },
    mounted() {
      this.loadDetail()
      if (this.$route.params.section) this.tabIndex = sections.indexOf(this.$route.params.section)
    },
    computed: {
      ...mapState('Docflow', ['detail', 'upload']),
      ...mapState('Account', ['account']),
      ...mapGetters('Account', [
        'isDocflowController',
        'isDocflowModerator',
        'isDocflowSupervisor',
        'isDocflowAssistant',
        'isDocflowPlanCreator',
        'isSuperUser'
      ]),
      editable() {
        return ['new', 'rejected', 'pending_resolution', 'resolution_filled'].includes(this.detail.state)
          && this.isAuthor
      },
      isAuthor() {
        return (this.detail.author && this.detail.author.id === this.account.employee.id) || this.isSuperUser
      },
      isSupervisor() {
        return (this.detail.supervisor && this.account.employee.id === this.detail.supervisor.id)
          || this.isDocflowSupervisor || this.isSuperUser
      },
      isResolutionAuthor() {
        return (this.detail.resolution_author && this.account.employee.id === this.detail.resolution_author.id)
          || this.isSuperUser
      },
      isResolutionApprover() {
        return (this.detail.resolution_approver && this.account.employee.id === this.detail.resolution_approver.id)
          || this.isSuperUser
      },
      isResolutionFilled() {
        return !!this.detail.resolution_approver
      },
      isReportsAvailable() {
        return ['resolution_approved', 'pending_closing', 'closed', 'in_process', 'cancelled'].includes(this.detail.state)
      },
      isCorrespondent() {
        return this.detail.correspondents ?
          this.detail.correspondents.filter(c => c.employee === this.account.employee.id).length > 0 : false
      },
      canSendChangeRequest() {
        return ['approved', 'in_process', 'registration_number_attached'].includes(this.detail.state)
          && (this.isAuthor || this.isResolutionAuthor || this.isResolutionApprover || this.isCorrespondent)
      },
      timeline() {
        let changeStates = this.detail.change_request_states.map(state => {
          let res = {
            ...timelineItem,
            date: state.created_at,
            title: this.$t(`workflow.changeStates.${state.state}`),
            note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
          }
          if (state.file !== null) {
            res.attachments = [{
              extension: 'doc',
              file: state.file,
              name: this.$t('docflow.changeRequest.file'),
              created_at: state.created_at,
              removable: false
            }]
          }
          return res
        });
        let documentStates = this.detail.states.map(state => ({
          ...timelineItem,
          date: state.created_at,
          isSigned: state.is_signed,
          title: this.$t(`workflow.states.${state.state}`),
          note: `${state.author ? state.author.initials : ''}${state.note ? ` – ${state.note}` : ''}`,
        }))
        return [...changeStates, ...documentStates].sort((a, b) => new Date(a.date) - new Date(b.date))
      },
      breadcrumbs() {
        return [
          {name: 'docflow'},
          {name: 'docflowIncoming', path: 'docflowIncoming'},
          {name: 'docflowDetail'}
        ]
      }
    }
  }
</script>

<style lang="scss" scoped>
  .dialog {
    &.big {
      .modal-card {
        width: 800px !important;
      }

      .input {
        resize: vertical;
        min-height: 300px;
      }
    }

    .input {
      resize: vertical;
      min-height: 80px;
    }
  }

  .edit {
    font-size: 13px !important;

    .icon {
      margin-right: 2px;
    }
  }

  table.information {
    width: 100%;
    table-layout: fixed;

    td {
      padding-bottom: 10px;
    }

    tr > td:first-child,
    tr > td:nth-child(3) {
      width: 140px;
    }

    tr > td:nth-child(2),
    tr > td:nth-child(4) {
      padding-right: 20px;
    }
  }

  .document.description {
    color: #666;

    & > div {
      margin-top: 10px;

      & > p {
        margin-top: 4px;
      }
    }
  }
</style>
